/**
 * Production-Ready Logger Service
 * 
 * Features:
 * - Environment-aware logging (dev vs production)
 * - Consistent log formatting
 * - Error tracking integration ready
 * - Performance monitoring
 * - Log levels (log, debug, info, warn, error)
 * 
 * Usage:
 * ```typescript
 * import { Logger } from '@/utils/logger'
 * 
 * Logger.log('User logged in', { userId: 123 })
 * Logger.error('API call failed', error)
 * Logger.debug('Debug info', data)
 * ```
 */

export enum LogLevel {
  DEBUG = 'debug',
  LOG = 'log',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogContext {
  [key: string]: any
}

export class Logger {
  private static isDev = import.meta.env.DEV
  private static isProd = import.meta.env.PROD
  private static appName = 'TubtimX'

  /**
   * Format log message with timestamp and context
   */
  private static formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const prefix = `[${this.appName}] [${timestamp}] [${level.toUpperCase()}]`
    
    if (context && Object.keys(context).length > 0) {
      return `${prefix} ${message} | Context: ${JSON.stringify(context)}`
    }
    
    return `${prefix} ${message}`
  }

  /**
   * Debug logging (only in development)
   * For detailed debugging information
   */
  static debug(message: string, context?: LogContext) {
    if (this.isDev) {
      console.debug(this.formatMessage(LogLevel.DEBUG, message, context))
    }
  }

  /**
   * General logging (only in development)
   * For general information
   */
  static log(message: string, context?: LogContext) {
    if (this.isDev) {
      console.log(this.formatMessage(LogLevel.LOG, message, context))
    }
  }

  /**
   * Info logging (only in development)
   * For informational messages
   */
  static info(message: string, context?: LogContext) {
    if (this.isDev) {
      console.info(this.formatMessage(LogLevel.INFO, message, context))
    }
  }

  /**
   * Warning logging (always enabled)
   * For potential issues
   */
  static warn(message: string, context?: LogContext) {
    console.warn(this.formatMessage(LogLevel.WARN, message, context))
    
    // ✅ Send to monitoring service in production
    if (this.isProd) {
      this.sendToMonitoring(LogLevel.WARN, message, context)
    }
  }

  /**
   * Error logging (always enabled)
   * For errors and exceptions
   */
  static error(message: string, error?: any, context?: LogContext) {
    const errorContext = {
      ...context,
      error: error?.message || error,
      stack: error?.stack,
    }
    
    console.error(this.formatMessage(LogLevel.ERROR, message, errorContext))
    
    // ✅ Send to error tracking service (Sentry, etc.)
    if (this.isProd) {
      this.sendToErrorTracking(message, error, errorContext)
    }
  }

  /**
   * Performance timing
   * Measure execution time of operations
   */
  static time(label: string) {
    if (this.isDev) {
      console.time(`[${this.appName}] ${label}`)
    }
  }

  /**
   * End performance timing
   */
  static timeEnd(label: string) {
    if (this.isDev) {
      console.timeEnd(`[${this.appName}] ${label}`)
    }
  }

  /**
   * Group logs together
   */
  static group(label: string, collapsed = false) {
    if (this.isDev) {
      if (collapsed) {
        console.groupCollapsed(`[${this.appName}] ${label}`)
      }
      else {
        console.group(`[${this.appName}] ${label}`)
      }
    }
  }

  /**
   * End log group
   */
  static groupEnd() {
    if (this.isDev) {
      console.groupEnd()
    }
  }

  /**
   * Table logging (useful for arrays/objects)
   */
  static table(data: any) {
    if (this.isDev) {
      console.table(data)
    }
  }

  /**
   * Send logs to monitoring service (production)
   * Integrate with your monitoring service (e.g., Datadog, New Relic)
   */
  private static sendToMonitoring(level: LogLevel, message: string, context?: LogContext) {
    // ✅ TODO: Integrate with monitoring service
    // Example: DatadogRUM, New Relic, etc.
    
    // For now, just store in localStorage for debugging
    try {
      const logs = JSON.parse(localStorage.getItem('app_logs') || '[]')
      logs.push({
        level,
        message,
        context,
        timestamp: new Date().toISOString(),
      })
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.shift()
      }
      
      localStorage.setItem('app_logs', JSON.stringify(logs))
    }
    catch (error) {
      // Silent fail
    }
  }

  /**
   * Send errors to error tracking service (production)
   * Integrate with Sentry or similar service
   */
  private static sendToErrorTracking(message: string, error: any, context?: LogContext) {
    // ✅ TODO: Integrate with Sentry or similar
    // Example:
    // Sentry.captureException(error, {
    //   tags: { level: 'error' },
    //   extra: { message, context }
    // });
    
    // For now, just store in localStorage for debugging
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]')
      errors.push({
        message,
        error: error?.message || String(error),
        stack: error?.stack,
        context,
        timestamp: new Date().toISOString(),
      })
      
      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.shift()
      }
      
      localStorage.setItem('app_errors', JSON.stringify(errors))
    }
    catch (err) {
      // Silent fail
    }
  }

  /**
   * Get stored logs (for debugging)
   */
  static getLogs() {
    try {
      return JSON.parse(localStorage.getItem('app_logs') || '[]')
    }
    catch {
      return []
    }
  }

  /**
   * Get stored errors (for debugging)
   */
  static getErrors() {
    try {
      return JSON.parse(localStorage.getItem('app_errors') || '[]')
    }
    catch {
      return []
    }
  }

  /**
   * Clear all stored logs
   */
  static clearLogs() {
    localStorage.removeItem('app_logs')
    localStorage.removeItem('app_errors')
  }
}

// ✅ Pre-configured logger instances for different modules
class ModuleLogger {
  constructor(private moduleName: string) {}

  debug(message: string, context?: LogContext) {
    Logger.debug(`[${this.moduleName}] ${message}`, context)
  }

  log(message: string, context?: LogContext) {
    Logger.log(`[${this.moduleName}] ${message}`, context)
  }

  info(message: string, context?: LogContext) {
    Logger.info(`[${this.moduleName}] ${message}`, context)
  }

  warn(message: string, context?: LogContext) {
    Logger.warn(`[${this.moduleName}] ${message}`, context)
  }

  error(message: string, error?: any, context?: LogContext) {
    Logger.error(`[${this.moduleName}] ${message}`, error, context)
  }

  time(label: string) {
    Logger.time(`[${this.moduleName}] ${label}`)
  }

  timeEnd(label: string) {
    Logger.timeEnd(`[${this.moduleName}] ${label}`)
  }
}

// ✅ Pre-configured loggers for common modules
export const authLogger = new ModuleLogger('AUTH')
export const apiLogger = new ModuleLogger('API')
export const socketLogger = new ModuleLogger('SOCKET')
export const cacheLogger = new ModuleLogger('CACHE')
export const logger = Logger

// ✅ Export as default for easier imports
export default Logger
